/**
 * Application Configuration
 * Centralized configuration for SpriteTool application
 * Provides default values that can be overridden via environment variables
 */

/**
 * Phaser game engine configuration
 */
export const PHASER_CONFIG = {
  // Frame rate configuration
  fps: {
    target: 10,
    min: 5,
    forceSetTimeOut: true,
  },

  // Renderer configuration
  renderer: {
    type: 'AUTO', // Phaser.AUTO
    antialias: true,
    roundPixels: true,
  },
};

/**
 * Session storage configuration
 */
export const STORAGE_CONFIG = {
  // localStorage key for sprite tools data
  storageKey: 'spriteToolsData',

  // Maximum age in days to keep data
  maxAge: 7,

  // Session restore behavior
  autoRestore: true,
};

/**
 * Sprite Editor UI configuration
 */
export const EDITOR_CONFIG = {
  // Z-index values for editor layers
  zIndex: {
    editor: 999999,
    toolbar: 1000001,
    modal: 1000000,
    overlay: 999998,
  },

  // Default zoom settings
  zoom: {
    default: 1.0,
    min: 0.1,
    max: 5.0,
    step: 0.1,
    factor: 1.1, // Multiplicative zoom factor for scroll wheel
  },

  // Grid configuration
  grid: {
    defaultVisible: false,
    color: '#333333',
    lineWidth: 1,
  },

  // Snap to pixel by default
  snapToPixel: true,

  // History management
  maxHistorySize: 50,
};

/**
 * Canvas rendering configuration
 */
export const CANVAS_CONFIG = {
  // Default background color
  backgroundColor: '#1a1a1a',

  // Selection colors
  selectionColor: '#00ff00',
  selectionWidth: 2,

  // Handle sizes
  handleSize: 8,
  handleColor: '#ffffff',
};

/**
 * Get configuration value with environment variable override
 * Supports dot notation for nested values (e.g., 'fps.target')
 *
 * @param {string} path - Configuration path (e.g., 'storage.maxAge')
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Configuration value
 */
export function getConfig(path, defaultValue) {
  const envKey = `VITE_${path.toUpperCase().replace(/\./g, '_')}`;

  // Check if environment variable exists
  if (import.meta.env[envKey] !== undefined) {
    return import.meta.env[envKey];
  }

  // Navigate the configuration object
  const keys = path.split('.');
  let value = {
    phaser: PHASER_CONFIG,
    storage: STORAGE_CONFIG,
    editor: EDITOR_CONFIG,
    canvas: CANVAS_CONFIG,
  };

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value !== undefined ? value : defaultValue;
}

/**
 * Export all configuration as a single object for convenience
 */
export default {
  phaser: PHASER_CONFIG,
  storage: STORAGE_CONFIG,
  editor: EDITOR_CONFIG,
  canvas: CANVAS_CONFIG,
  getConfig,
};
